import {
  Heading,
  Inline,
  InlineItem,
  Placeholder,
  Secondary,
  Stack,
  Text,
} from '@kinesis/bungle';
import { useInventoryId } from '@/hooks/use-inventory-id';
import { useGetInventoryQuery } from '@/api/inventories';
import { useGetEmissionsQuery } from '@/api/emissions';
import { useGetPlacesQuery } from '@/api/places';
import { Layout } from '@/components/layout';
import { Loading } from '@/components/loading';
import { InventoryEmissionsChart } from '@/components/inventory-emissions-chart';
import { GeographicalScope } from '@/components/geographical-scope';
import { CalloutCard } from '@/components/callout-card';
import { disclosuresPath, pathwaysPath } from '@/links';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// The Inventory Home — the default landing. The mental model: Inventory is
// the *state of emissions* you land on; Disclosures are the artifacts you cut
// from it; Scenarios are the inputs. Layout: header → one stacked-by-source
// annual chart → two call-outs (create a disclosure; start a pathway) → the
// geographical scope.
//
// Owns the RTK queries + the data states per the page/component split; the
// chart, scope, and call-outs are store-less presentational components. The
// reporting window + display unit are resolved server-side (`GET
// /inventories/:id/emissions`); the caption states the boundary once ("Years
// ending {Month}") — never "FY".
const InventoryHome = () => {
  const inventoryId = useInventoryId();
  const { data: inventory, isLoading: inventoryLoading } =
    useGetInventoryQuery(inventoryId);
  const { data: emissions, isLoading: emissionsLoading } =
    useGetEmissionsQuery(inventoryId);
  const { data: places, isLoading: placesLoading } =
    useGetPlacesQuery(inventoryId);

  if (inventoryLoading || !inventory) {
    return (
      <Layout>
        <Loading />
      </Layout>
    );
  }

  const hasEmissions = !!emissions && emissions.years.length > 0;

  return (
    <Layout>
      <Stack space='large'>
        <Stack space='xsmall'>
          <Heading as='h2' size='large'>
            {inventory.name}
          </Heading>
          <Text tone='secondary'>Community emissions inventory</Text>
        </Stack>

        <Stack space='small'>
          <Stack space='xsmall'>
            <Heading as='h4' size='small'>
              Emissions over time
            </Heading>
            {hasEmissions && (
              <Secondary>{`Years ending ${MONTHS[emissions.endMonth - 1] ?? ''}`}</Secondary>
            )}
          </Stack>
          {emissionsLoading ? (
            <Loading />
          ) : hasEmissions ? (
            <InventoryEmissionsChart emissions={emissions} />
          ) : (
            <Placeholder>
              No activity has been recorded for this inventory yet.
            </Placeholder>
          )}
        </Stack>

        <Inline space='medium'>
          <InlineItem sizing='fill-container'>
            <CalloutCard
              title='Create a disclosure'
              description='Report your community emissions as a GPC or community report.'
              to={disclosuresPath(inventoryId)}
            />
          </InlineItem>
          <InlineItem sizing='fill-container'>
            <CalloutCard
              title='Start a pathway'
              description='Model a decarbonisation pathway of scenarios and interventions.'
              to={pathwaysPath(inventoryId)}
            />
          </InlineItem>
        </Inline>

        {placesLoading ? (
          <Loading />
        ) : (
          <GeographicalScope places={places ?? []} />
        )}
      </Stack>
    </Layout>
  );
};

export { InventoryHome };
